import type { GapInboxItem, GapSolutionResponse } from "../types";
import type { KbGapInboxSectionProps } from "./kbGapInboxTypes";

type Props = {
  item: GapInboxItem;
  section: KbGapInboxSectionProps;
  activeSuggestionId: string | null;
  loadingSuggestionId: string | null;
  suggestionByItemId: Record<string, GapSolutionResponse | undefined>;
  onToggleSuggestion: (item: GapInboxItem) => void;
};

export function KbGapInboxItemCard({
  item,
  section: props,
  activeSuggestionId,
  loadingSuggestionId,
  suggestionByItemId,
  onToggleSuggestion,
}: Props) {
  return (
    <div
      className={
        "rounded-xl border px-3 py-3 space-y-2 " +
        (item.item_type === "link_review"
          ? "border-sky-200 bg-sky-50/60 dark:border-sky-800 dark:bg-sky-950/30"
          : item.status === "dismissed"
            ? "border-gray-200 bg-gray-50 opacity-60 dark:border-zinc-700 dark:bg-zinc-900/40"
            : item.status === "done"
              ? "border-emerald-200 bg-emerald-50/40 opacity-70 dark:border-emerald-800 dark:bg-emerald-950/30"
              : "border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-950/30")
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">{item.title}</div>
          {item.source_article_slug ? (
            <div className="text-[11px] text-gray-500 mt-0.5 dark:text-zinc-400 flex flex-wrap items-center gap-2">
              <span>from: {item.source_article_slug}</span>
              {props.onOpenSourceArticle ? (
                <button
                  type="button"
                  className="rounded border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-800 dark:border-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-100"
                  onClick={() => props.onOpenSourceArticle!(item.source_article_slug!)}
                >
                  Open in editor
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={
              "text-[11px] rounded-full border px-2 py-0.5 " +
              (item.item_type === "link_review"
                ? "border-sky-300 bg-white text-sky-900 dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-100"
                : item.item_type === "query_miss"
                  ? "border-violet-300 bg-white text-violet-900 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-100"
                  : "border-amber-300 bg-white text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-100")
            }
          >
            {item.item_type === "link_review" ? "link review" : item.item_type === "query_miss" ? "query miss" : "gap"}
          </span>
          <span
            className={
              "text-[11px] rounded-full border px-2 py-0.5 " +
              (item.priority === "high"
                ? "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100"
                : item.priority === "low"
                  ? "border-gray-300 bg-gray-100 text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                  : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100")
            }
          >
            {item.priority}
          </span>
        </div>
      </div>

      {item.summary ? <div className="text-xs text-gray-700 dark:text-zinc-300">{item.summary}</div> : null}
      {item.article_draft.trim() ? (
        <div className="text-[11px] text-gray-600 line-clamp-2 whitespace-pre-wrap dark:text-zinc-400">
          {(() => {
            const draft = item.article_draft.trim();
            const substantive =
              draft
                .split(/\n+/)
                .map((l) => l.trim())
                .find((l) => l.length > 0 && !l.startsWith("#")) || draft;
            const clipped = substantive.length > 280 ? `${substantive.slice(0, 279)}…` : substantive;
            return clipped;
          })()}
        </div>
      ) : null}

      {item.linked_article_slugs.length > 0 ? (
        <div className="text-[11px] text-gray-500 dark:text-zinc-400">
          linked:{" "}
          {item.linked_article_slugs.map((link) => (
            <button
              key={link.slug}
              type="button"
              className="underline text-indigo-700 mr-2 dark:text-indigo-300"
              onClick={() => props.onPreviewRelatedArticle(link.slug, link.slug)}
            >
              {link.slug}
            </button>
          ))}
        </div>
      ) : null}

      {item.article_draft.trim() ? (
        <details className="rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900/50">
          <summary className="cursor-pointer px-3 py-2 text-xs text-gray-600 select-none dark:text-zinc-300">View draft</summary>
          <div className="px-3 py-2 whitespace-pre-wrap text-xs text-gray-700 max-h-48 overflow-y-auto border-t border-gray-100 dark:border-zinc-700 dark:text-zinc-300">
            {item.article_draft}
          </div>
        </details>
      ) : null}

      <div className="flex flex-wrap gap-2 pt-1">
        {item.status !== "in_progress" ? (
          <button
            type="button"
            className="rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs text-indigo-900 dark:border-indigo-700 dark:bg-indigo-950/45 dark:text-indigo-100"
            onClick={() => props.onUpdateGapStatus(item.id, "in_progress")}
          >
            Mark in progress
          </button>
        ) : null}
        {item.status !== "accepted" ? (
          <button
            type="button"
            className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
            onClick={() => props.onUpdateGapStatus(item.id, "accepted")}
          >
            Accept
          </button>
        ) : null}
        {item.status !== "done" ? (
          <button
            type="button"
            className="rounded-lg border border-emerald-400 bg-emerald-100 px-3 py-1.5 text-xs text-emerald-900 dark:border-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-100"
            onClick={() => props.onUpdateGapStatus(item.id, "done")}
          >
            Mark done
          </button>
        ) : null}
        {item.status !== "dismissed" ? (
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
            onClick={() => props.onUpdateGapStatus(item.id, "dismissed")}
          >
            Dismiss
          </button>
        ) : null}
        {item.status !== "open" ? (
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
            onClick={() => props.onUpdateGapStatus(item.id, "open")}
          >
            Reopen
          </button>
        ) : null}
        {item.status === "open" || item.status === "in_progress" || item.status === "accepted" ? (
          <button
            type="button"
            className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-900 dark:border-violet-700 dark:bg-violet-950/45 dark:text-violet-100"
            onClick={() => void onToggleSuggestion(item)}
          >
            Suggest solution
          </button>
        ) : null}
        {item.status === "open" || item.status === "in_progress" || item.status === "accepted" ? (
          <button
            type="button"
            className="rounded-lg border border-indigo-400 bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white"
            onClick={() => props.onAddressGapItem(item)}
          >
            Address this gap →
          </button>
        ) : null}
        <div className="self-center text-[11px] text-gray-400 dark:text-zinc-500">
          {item.submitted_by ? `submitted by ${item.submitted_by} · ` : ""}
          {item.status}
        </div>
      </div>
      {activeSuggestionId === item.id ? (
        <div className="rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-3 space-y-3 dark:border-violet-800 dark:bg-violet-950/25">
          {loadingSuggestionId === item.id ? (
            <div className="text-xs text-violet-800 dark:text-violet-100">Loading related article suggestions…</div>
          ) : null}
          {suggestionByItemId[item.id] ? (
            <>
              <div className="rounded-lg border border-violet-200 bg-white px-3 py-2 text-xs text-violet-900 dark:border-violet-700 dark:bg-zinc-900/60 dark:text-violet-100">
                <div className="font-semibold">What to add or fix (plain language)</div>
                <div className="mt-1 leading-relaxed">{suggestionByItemId[item.id]?.missing_article_blueprint.summary}</div>
                {(suggestionByItemId[item.id]?.missing_article_blueprint.required_terms || []).length ? (
                  <div className="mt-2 text-[11px] text-gray-600 dark:text-zinc-400">
                    <span className="font-medium text-gray-700 dark:text-zinc-300">Topics to cover: </span>
                    {(suggestionByItemId[item.id]?.missing_article_blueprint.required_terms || []).slice(0, 6).join(" · ")}
                  </div>
                ) : null}
                <div className="mt-1 text-[10px] text-gray-500 dark:text-zinc-500">
                  Suggested doc type: {suggestionByItemId[item.id]?.missing_article_blueprint.doc_type_hint || "unknown"}
                </div>
              </div>
              {(suggestionByItemId[item.id]?.suggestions || []).length === 0 ? (
                <div className="text-xs text-gray-600 dark:text-zinc-300">
                  No high-confidence related articles were found. Use “Address this gap” to draft a new article.
                </div>
              ) : (
                <div className="space-y-2">
                  {(suggestionByItemId[item.id]?.suggestions || []).map((candidate) => (
                    <div key={candidate.slug} className="rounded-lg border border-violet-200 bg-white px-3 py-2 text-xs space-y-2 dark:border-violet-800 dark:bg-zinc-900/60">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-zinc-100">{candidate.title}</div>
                          <div className="text-gray-500 dark:text-zinc-400">{candidate.slug}</div>
                        </div>
                        <div className="text-right">
                          <div className="rounded-full border border-violet-300 px-2 py-0.5 text-[11px] text-violet-900 dark:border-violet-700 dark:text-violet-100">
                            {candidate.confidence}% match
                          </div>
                          <div className="mt-1 text-[11px] text-gray-500 dark:text-zinc-400">
                            {candidate.suggested_action === "revise" ? "edit existing" : "reference existing"}
                          </div>
                        </div>
                      </div>
                      <div className="text-gray-700 dark:text-zinc-300">{candidate.reason}</div>
                      <div className="text-[11px] text-gray-500 dark:text-zinc-400">
                        type {candidate.doc_type || "(none)"} • scope {candidate.scope || "(none)"} • status {candidate.status}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                          onClick={() => props.onPreviewRelatedArticle(candidate.slug, candidate.title)}
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-indigo-300 bg-indigo-50 px-2 py-1 text-[11px] text-indigo-900 dark:border-indigo-700 dark:bg-indigo-950/45 dark:text-indigo-100"
                          onClick={() => props.onOpenRelatedArticle(candidate.slug, candidate.title)}
                        >
                          Open to edit
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
                          onClick={() => void props.onResolveGapByLink(item, candidate.slug)}
                        >
                          Mark done by linking
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
